import {
	AfterViewInit,
	Component,
	Input,
	OnChanges,
	output,
	SimpleChange,
} from '@angular/core';
import { ComponentBase, DeploymentContext } from '@Common';
import * as d3 from 'd3';
import { LegendData } from './legend/legend.component';

export interface StackedBarData {
	group: string;
	category: string;
	value: number;
}

export interface StackedBarUserStyle {
	width?: number;
	marginTop?: number;
	marginRight?: number;
	marginBottom?: number;
	marginLeft?: number;
	fontSize?: string;
}

export interface StackedBarStyle extends StackedBarUserStyle {
	// Calculated styles should not be set by the user
	height?: number;
}
export interface StackedBarUserOptions {
	style: StackedBarUserStyle;
	barHeight: number;
}

export interface StackedBarOptions extends StackedBarUserOptions {
	style: StackedBarStyle;
	labelPadding: number;
}
@Component({
	selector: 'stacked-bar-chart',
	templateUrl: './stacked-bar-chart.component.html',
	styleUrls: ['./stacked-bar-chart.component.scss'],
	standalone: false,
})
export class StackedBarChartComponent
	extends ComponentBase
	implements OnChanges, AfterViewInit
{
	@Input()
	public colorMap: (category: string) => string;

	@Input()
	public data: StackedBarData[] = [];

	@Input()
	public userOptions: StackedBarUserOptions;

	@Input()
	public xAxisLabel: string;

	@Input()
	public yAxisLabel: string;

	@Input()
	public title: string;

	@Input()
	public lowestYGroup: string = null;

	@Input()
	public itemLabel: string;

	@Input()
	/**
	 * The strings, in order, to be used as the legend below the chart. These must
	 * return the same value from the colorMap as the corresponding data in the data
	 * for the legend to be accurate. If no legendLabels are provided, they will be
	 * automtically generated but the order is not configurable.
	 */
	public legendLabels: string[];

	public id: string;

	public get style() {
		return this.options.style;
	}

	public get fontInPx(): number {
		return Number(this.style.fontSize.replace('px', ''));
	}

	public onCategorySelected = output<{ category: string; group: string }>();

	protected hasData: boolean = false;
	protected options: StackedBarOptions;
	protected series;
	protected chart;
	protected xScale;
	protected yScale;

	public constructor(dc: DeploymentContext) {
		super(dc);
		// This originally used crypto.randomUUID() but that wasn't always available in all contexts,
		// the below is more widely supported and achieves the same goal of just generating a unique ID.
		this.id = `chart-${Math.random().toString(36).substring(2, 8)}`;
	}

	public ngOnChanges(changes: { data?: SimpleChange }): void {
		// The data is the only input change that requires recalculation,
		// everything else is handled through the standard angular templating
		if (!changes.data) {
			return;
		}

		const prevData = changes.data.previousValue;
		const currentData = changes.data.currentValue;

		// If the length has changed (there are a different number of group/category pairings)
		// or there is any pairing that existis in the new data that was not in the old, we
		// need to recalculate the series backing the chart.
		if (
			currentData?.length !== prevData?.length ||
			currentData?.some((current: StackedBarData) => {
				return (
					prevData?.find(
						(previous: StackedBarData) =>
							current.group === previous.group &&
							current.category === previous.category
					)?.value !== current?.value
				);
			})
		) {
			this.initializeData();
		}
	}

	public ngAfterViewInit(): void {
		this.initializeData();
	}

	public groupTotals: Map<string, number>;
	public categoryTotals: Map<string, number>;
	public itemsForLegend: LegendData[];
	public initializeData(): void {
		this.hasData = this.data.length > 0;
		if (!this.hasData) {
			// If this is due to filtering out all data on explore,
			// remove the prior chart
			this.deleteSvg();
			return;
		}

		// Pre-sort the data so that the largest groups will be at the
		// top of the Y axis while preserving the original order of the
		// X axis.
		this.groupTotals = this.getTotalsByGroup();
		this.categoryTotals = this.getTotalsByCategory();

		this.data.sort((a, b) => {
			if (a.group === this.lowestYGroup) {
				return 1;
			}
			if (b.group === this.lowestYGroup) {
				return -1;
			}

			return (
				this.groupTotals.get(b.group) - this.groupTotals.get(a.group)
			);
		});

		// Determine the label, count and color for each item in the legend
		this.legendLabels =
			this.legendLabels ??
			this.data.reduce((acc, val) => {
				if (!acc.includes(val.category)) {
					acc.push(val.category);
				}
				return acc;
			}, []);

		this.itemsForLegend = this.legendLabels
			?.map((category) => {
				return {
					label: category,
					count: this.categoryTotals.get(category),
					style: this.getSwatchStyle(category),
				};
			})
			.filter((i) => i.count > 0);
		// Ensure we use defualts for anything not user provided
		this.hydrateOptions();
		// Determine the series that need to be stacked.
		this.series = this.getSeriesFromData();

		this.hasData = this.series.length > 0;
		if (!this.hasData) {
			this.deleteSvg();
			return;
		}

		// Compute the height from the number of stacks.
		this.style.height =
			this.series[0]?.length * this.options.barHeight +
			this.style.marginTop +
			this.style.marginBottom;

		// Prepare the scales for positional encodings.
		this.xScale = this.getXScale();
		this.yScale = this.getYScale();

		this.deleteSvg();

		// Create the SVG container.
		this.chart = this.createSvg();

		// Append a group for each series, and a rect for each element in the series.
		this.addGroupsAndRects();

		this.addRowTotals();

		// Append the horizontal axis.
		this.addXAxis();

		// Append the vertical axis.
		this.addYAxis();
	}

	public getSwatchStyle(key: string): Partial<CSSStyleDeclaration> {
		return {
			height: '15px',
			width: '15px',
			backgroundColor: this.colorMap(key),
		};
	}

	public handleSelection(category: string, group: string) {
		this.onCategorySelected.emit({ category, group });
	}

	/** Suplements user-provided options with defualts for any options not provided */
	private hydrateOptions() {
		this.options = {
			style: {
				width: 1000,
				marginTop: 30,
				marginRight: 50,
				marginBottom: 0,
				marginLeft: 200,
				fontSize: '12px',
				...this.userOptions?.style,
			},
			labelPadding: 2,
			barHeight: 35,
		};
	}

	private getYScale() {
		return d3
			.scaleBand()
			.domain(this.data.map((d) => d.group))
			.range([
				this.style.marginTop,
				this.style.height - this.style.marginBottom,
			])
			.padding(0.1);
	}

	private getXScale() {
		return d3
			.scaleLinear()
			.domain([0, d3.max(this.series, (d) => d3.max(d, (d) => d[1]))])
			.range([
				this.style.marginLeft,
				this.style.width - this.style.marginRight,
			]);
	}

	private getSeriesFromData() {
		return (
			d3
				.stack()
				// distinct series keys, in input order
				.keys(
					this.legendLabels ||
						d3.union(this.data.map((d) => d.category))
				)
				// get value for each series key and stack
				.value(([, D], key) => D.get(key)?.value ?? 0)(
				// group by stack then series key
				d3.index(
					this.data,
					(d) => d.group,
					(d) => d.category
				)
			)
		);
	}

	private getTotalsByGroup(): Map<string, number> {
		return d3.rollup(
			this.data,
			(v) => d3.sum(v, (d) => d.value),
			(d) => d.group
		);
	}

	private getTotalsByCategory(): Map<string, number> {
		return d3.rollup(
			this.data,
			(v) => d3.sum(v, (d) => d.value),
			(d) => d.category
		);
	}

	public formatValue(x) {
		return isNaN(x) ? 'N/A' : x.toLocaleString('en');
	}

	private deleteSvg() {
		d3.select(`#${this.id}`).selectAll('*').remove();
	}

	private createSvg() {
		return d3
			.select(`#${this.id}`)
			.append('svg')
			.attr('width', this.style.width)
			.attr('height', this.style.height)
			.attr('viewBox', [0, 0, this.style.width, this.style.height])
			.attr('class', 'chart');
	}

	private addGroupsAndRects() {
		// Set local variables off of `this` to ensure access inside
		// some of the nested functions where `this` does not refer
		// to the component itself
		const chart = this.chart;
		const options = this.options;

		const groups = this.chart
			.append('g')
			.selectAll('g')
			.data(this.series)
			.join('g')
			.attr('fill', (d) => this.colorMap(d.key));

		groups
			.selectAll('rect')
			.data((D) => D.map((d) => ((d.key = D.key), d)))
			.join('rect')
			.attr('x', (d) => this.xScale(d[0]))
			.attr('y', (d) => this.yScale(d.data[0]))
			.attr('category', (d) => d.data[0])
			.attr('group', (d) => d.key)
			.attr('height', this.yScale.bandwidth())
			.attr('width', (d) => this.xScale(d[1]) - this.xScale(d[0]))
			.append('title')
			.text((d) => this.getToolTip(d));

		// Add the label of the value of the group to show over the rect
		// The font size needs to be manually set or the computations will be off
		groups
			.selectAll('text')
			.data((D) => D.map((d) => ((d.key = D.key), d)))
			.join('text')
			.text((d) => {
				return this.formatValue(d.data[1].get(d.key)?.value ?? 0);
			})
			.attr('category', (d) => d.data[0])
			.attr('group', (d) => d.key)
			.attr('x', (d) => (this.xScale(d[0]) + this.xScale(d[1])) / 2)
			.attr(
				'y',
				(d) =>
					this.yScale(d.data[0]) +
					this.yScale.bandwidth() / 2 +
					this.fontInPx / 3
			)
			.attr('class', 'bar-label')
			.attr('font-size', `${this.fontInPx}px`)
			.each(function () {
				const text = d3.select(this);
				const textWidth = text.node().getComputedTextLength();

				const maxWidth = chart
					.select(
						`rect[group="${text.attr(
							'group'
						)}"][category="${text.attr('category')}"]`
					)
					.node()
					?.getBBox().width;

				// Hide label if it won't fit
				if (textWidth + options.labelPadding > maxWidth) {
					text.remove();
				}
			});

		groups.selectAll('rect').on('click', (event) => {
			this.onCategorySelected.emit({
				category: event.target.attributes.category.value,
				group: event.target.attributes.group.value,
			});
		});
	}

	private addRowTotals() {
		// For each group, find the stacked bar's end (max x)
		const rowEnds = new Map<string, number>();

		this.series.forEach((series) => {
			series.forEach((d) => {
				const group = d.data[0];
				const currentMax = rowEnds.get(group) ?? 0;
				rowEnds.set(group, Math.max(currentMax, d[1]));
			});
		});

		this.chart
			.append('g')
			.attr('class', 'row-totals')
			.selectAll('text')
			.data([...this.groupTotals.entries()])
			.join('text')
			.text(([, total]) => this.formatValue(total))
			.attr(
				'x',
				([group]) => this.xScale(rowEnds.get(group) ?? 0) + 6 // padding after bar
			)
			.attr(
				'y',
				([group]) =>
					this.yScale(group)! +
					this.yScale.bandwidth() / 2 +
					this.fontInPx / 3
			)
			.attr('font-size', this.style.fontSize)
			.attr('fill', 'black')
			.attr('class', 'row-total-label');
	}

	public getToolTip(d): string {
		return `${d.data[0]} : ${d.key}\n${this.formatValue(
			d.data[1].get(d.key)?.value ?? 0
		)}`;
	}

	private addYAxis() {
		this.chart
			.append('g')
			.attr('transform', `translate(${this.style.marginLeft},0)`)
			.call(d3.axisLeft(this.yScale).tickSizeOuter(0))
			.call((g) => g.selectAll('.domain').remove())
			.selectAll('text')
			.attr('class', 'y-tick-label')
			.style('font-size', this.style.fontSize);

		this.chart
			.append('text')
			.attr('text-anchor', 'middle')
			.attr('transform', `rotate(-90)`)
			.attr(
				'x',
				-(
					(this.style.height -
						this.style.marginTop -
						this.style.marginBottom) /
						2 +
					this.style.marginTop
				)
			)
			.attr('y', 12)
			.attr('class', 'y-label')
			.attr('fill', 'black')
			.attr('font-size', '18px')
			.text(this.yAxisLabel);
	}

	private addXAxis() {
		this.chart
			.append('g')
			.attr('transform', `translate(0,${this.style.marginTop})`)
			.call(d3.axisTop(this.xScale).ticks(this.style.width / 100, 's'))
			.call((g) => g.selectAll('.domain').remove());
	}
}
